import { useAppSelector } from '@/hooks/store';
import { useDemoMode } from '@/hooks/useDemoMode';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import {
  clearDemoMode,
  isDemoModeAllowedOnCurrentPage,
  isDemoModeReadonlyPage,
} from '@/utils/demo-mode';
import { EyeIcon } from '@/utils/helpers';
import { InputSwitch } from 'primereact/inputswitch';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Banner = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: var(--medium-padding) var(--default-padding);
  margin-bottom: var(--default-padding);
  border-radius: var(--semi-medium-border-radius);
  background-color: ${({ $isActive }) =>
    $isActive ? 'var(--green-opacity-lighter)' : 'var(--orange-pale)'};

  svg {
    flex-shrink: 0;

    path {
      stroke: ${({ $isActive }) =>
        $isActive ? 'var(--green-main)' : 'var(--orange-main)'};
    }
  }

  .p-inputswitch {
    width: 59px;
    height: 26px;
    min-height: 26px;
    margin-left: auto;

    .p-inputswitch-slider:before {
      height: 20px;
      width: 20px;
    }

    &::after {
      top: 4px;
      font-size: var(--xsmall-font-size);
    }

    &.p-inputswitch-checked .p-inputswitch-slider:before {
      transform: translateX(33px);
    }
  }
`;

const TextContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const DemoModeToggle: React.FC = () => {
  const { t } = useTranslation();
  const { isActive, handleToggle } = useDemoMode();
  const currentAccount = useAppSelector(selectCurrentAccount);

  const isReadonlyPage = isDemoModeReadonlyPage();

  if (!currentAccount?.freeTrialEndsAt) {
    clearDemoMode();
    return null;
  }

  if (!isDemoModeAllowedOnCurrentPage()) {
    return null;
  }

  if (isReadonlyPage && isActive) {
    return (
      <Banner $isActive={true}>
        <EyeIcon />
        <TextContainer>
          <p
            className="m-0"
            dangerouslySetInnerHTML={{ __html: t('demoMode.readonly.message') }}
          />
        </TextContainer>
      </Banner>
    );
  }

  if (isReadonlyPage) {
    return null;
  }

  return (
    <Banner $isActive={isActive}>
      <EyeIcon />
      <TextContainer>
        {isActive ? (
          <p
            className="m-0"
            dangerouslySetInnerHTML={{ __html: t('demoMode.on.description') }}
          />
        ) : (
          <p className="m-0">{t('demoMode.off.description')}</p>
        )}
      </TextContainer>
      <ToggleWrapper>
        <InputSwitch
          checked={isActive}
          className="v2"
          onChange={(e) => handleToggle(e.value)}
        />
      </ToggleWrapper>
    </Banner>
  );
};
